import {
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'

import { ValidationResponseTypes, ValidationTypes } from 'shared/ui/form/inputs'
import PasswordField from 'shared/ui/form/PasswordField'
import TextField from 'shared/ui/form/TextField'
import { shouldPreventKeyForNumberInput } from 'shared/utils/form'
import { isNotNil, isNotNilOrEmpty } from 'shared/utils/ramda'

interface InputProps {
  id?: string
  onBlur?: () => void
  onFocus?: () => void
  onClick?: () => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  withPasswordHint?: boolean
  onChange?: (name: string, value: string) => void
  name: string
  type?: string
  inputProps?: InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>
  value: string
  label?: string
  placeholder?: string
  validate?: (
    name: string,
    callback: (v: ValidationResponseTypes) => void
  ) => {}
  disabled?: boolean
  icons?: {
    start?: React.ReactElement | null
    end?: React.ReactElement | null
  }
  resetValidationTrigger?: number
  required?: boolean
  noMargin?: boolean
  ecoVariant?: boolean
  size?: 'medium' | 'large'
  autoFocus?: boolean
  autoComplete?: 'off'
  invalid?: boolean | string
  rows?: number
  inputMode?:
    | 'text'
    | 'search'
    | 'email'
    | 'tel'
    | 'url'
    | 'none'
    | 'numeric'
    | 'decimal'
}

const Input = ({
  id,
  onBlur,
  onFocus,
  onChange,
  withPasswordHint,
  name,
  inputProps,
  type,
  value: initialValue,
  placeholder,
  validate,
  disabled,
  resetValidationTrigger,
  noMargin,
  autoFocus,
  autoComplete,
  inputMode,
  invalid,
  onKeyDown,
  rows,
  ...rest
}: InputProps): React.ReactElement => {
  const [value, setValue] = useState(initialValue || '')
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    isNotNil(initialValue) && setValue(initialValue)
  }, [initialValue])

  const [{ valid, error }, _validate] = useState<ValidationTypes>({
    valid: !validate,
    error: ''
  })

  const validateRef = useRef(validate)

  validateRef.current = validate

  const runValidation = useCallback((currentName: string) => {
    if (typeof validateRef.current === 'function') {
      validateRef.current(currentName, (v: ValidationResponseTypes) => {
        _validate({ valid: v.valid, error: v.message })
      })
    }
  }, [])

  useEffect(() => {
    runValidation(name)
  }, [value, name, runValidation])

  useEffect(() => {
    isNotNilOrEmpty(resetValidationTrigger) && setTouched(false)
  }, [resetValidationTrigger])

  const handleBlur = () => {
    setTouched(true)
    typeof onBlur === 'function' && onBlur()
  }

  const handleFocus = () => {
    typeof onFocus === 'function' && onFocus()
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValue(e.target.value)
    typeof onChange === 'function' && onChange(name, e.target.value)
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    shouldPreventKeyForNumberInput({ type, key: event.key }) &&
      event.preventDefault()
    onKeyDown?.(event as React.KeyboardEvent<HTMLInputElement>)
  }

  if (type === 'password') {
    return (
      <PasswordField
        id={id}
        error={(validate && !valid && touched && !disabled) || Boolean(invalid)}
        helperText={(validate && !valid && touched && error) || invalid}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onChange={handleChange}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        onKeyDown={handleKeyDown}
        name={name}
        inputMode={inputMode}
        value={value}
        withPasswordHint={withPasswordHint}
        placeholder={placeholder}
        disabled={disabled}
        inputProps={inputProps}
        marginBottom={!noMargin}
        {...rest}
      />
    )
  }

  return (
    <TextField
      id={id}
      error={(validate && !valid && touched && !disabled) || Boolean(invalid)}
      helperText={(validate && !valid && touched && error) || invalid}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onChange={handleChange}
      autoFocus={autoFocus}
      autoComplete={autoComplete}
      onKeyDown={handleKeyDown}
      name={name}
      inputMode={inputMode}
      type={type || 'text'}
      value={value}
      rows={rows}
      marginBottom={!noMargin}
      placeholder={placeholder}
      disabled={disabled}
      inputProps={inputProps}
      {...rest}
    />
  )
}

export default Input
